import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { PageHeader } from '@/components/AIBadge';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
    DollarSign, Loader2, Search, TrendingUp, AlertTriangle, Shield, Layers, Check, X, Edit3, Package as PackageIcon,
} from 'lucide-react';

const RISK_META = {
    low: { label: 'Bajo', color: '#00875A', icon: Shield },
    medium: { label: 'Medio', color: '#F59E0B', icon: TrendingUp },
    high: { label: 'Alto', color: '#EF4444', icon: AlertTriangle },
};

const money = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0);

export default function AdminPricing() {
    const [data, setData] = useState({ packages: [], rows: [] });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [riskFilter, setRiskFilter] = useState('all');
    const [editRow, setEditRow] = useState(null); // rubro row being edited

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('/admin/pricing/matrix');
            setData(r.data);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al cargar matriz'));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const query = q.trim().toLowerCase();
        return data.rows.filter((r) => {
            if (riskFilter !== 'all' && r.risk_level !== riskFilter) return false;
            if (query && !(`${r.rubro_name} ${r.category}`.toLowerCase().includes(query))) return false;
            return true;
        });
    }, [data.rows, q, riskFilter]);

    const stats = useMemo(() => {
        const total = data.rows.length;
        const byRisk = { low: 0, medium: 0, high: 0 };
        let avgBase = 0;
        for (const r of data.rows) {
            if (byRisk[r.risk_level] != null) byRisk[r.risk_level] += 1;
            avgBase += r.base_price_mxn || 0;
        }
        return { total, byRisk, avgBase: total ? avgBase / total : 0 };
    }, [data.rows]);

    return (
        <AdminLayout title="Matriz de Precios">
            <div className="max-w-[1400px] space-y-6">
                <PageHeader
                    icon={DollarSign}
                    title="Matriz de Precios por Rubro"
                    subtitle="Define el precio base y nivel de riesgo por rubro. Los precios de cada paquete se calculan automáticamente aplicando el multiplicador."
                    accent="#7C6BEF"
                    data-testid="admin-pricing-header"
                />

                {/* Paquetes de referencia */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {data.packages.map((p) => (
                        <div key={p.code} className="p-4 border-2 relative"
                            style={{ borderColor: p.color || '#7C6BEF', background: `${p.color || '#7C6BEF'}08` }}
                            data-testid={`pkg-ref-${p.code}`}>
                            {p.is_popular && (
                                <div className="absolute top-2 right-2 text-[9px] font-bold px-2 py-0.5 uppercase tracking-widest text-white"
                                    style={{ background: p.color }}>
                                    Más elegido
                                </div>
                            )}
                            <div className="flex items-center gap-2 mb-1">
                                <PackageIcon className="w-4 h-4" style={{ color: p.color }} />
                                <span className="text-[10px] uppercase tracking-widest font-bold opacity-60">{p.code}</span>
                            </div>
                            <div className="text-2xl font-display font-black" style={{ color: p.color }}>{p.name}</div>
                            <div className="text-xs opacity-70 mt-1">Multiplicador <b>×{p.price_multiplier}</b></div>
                            <div className="text-xs opacity-70">
                                Recomendaciones IA: <b>{p.ai_recommendations_per_audit === -1 ? 'Ilimitadas' : p.ai_recommendations_per_audit}</b>
                            </div>
                        </div>
                    ))}
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <KpiBox label="Rubros" value={stats.total} />
                    <KpiBox label="Bajo riesgo" value={stats.byRisk.low} color="#00875A" />
                    <KpiBox label="Medio riesgo" value={stats.byRisk.medium} color="#F59E0B" />
                    <KpiBox label="Alto riesgo" value={stats.byRisk.high} color="#EF4444" />
                </div>

                {/* Filtros */}
                <div className="flex flex-wrap gap-2 items-center">
                    <div className="flex items-center border-2 px-2 py-1 flex-1 min-w-[200px]"
                        style={{ borderColor: 'var(--border)' }}>
                        <Search className="w-4 h-4 opacity-60" />
                        <input type="text" placeholder="Buscar rubro..." value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="pricing-search-input"
                            className="bg-transparent outline-none px-2 py-1 flex-1 text-sm" />
                    </div>
                    <div className="flex gap-1">
                        {[
                            { v: 'all', label: 'Todos' },
                            { v: 'low', label: 'Bajo' },
                            { v: 'medium', label: 'Medio' },
                            { v: 'high', label: 'Alto' },
                        ].map((f) => (
                            <button key={f.v} onClick={() => setRiskFilter(f.v)}
                                data-testid={`pricing-filter-${f.v}`}
                                className="text-xs font-bold uppercase tracking-widest px-3 py-2 border-2"
                                style={{
                                    background: riskFilter === f.v ? '#111' : 'transparent',
                                    color: riskFilter === f.v ? '#fff' : 'inherit',
                                    borderColor: 'var(--border)',
                                }}>
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tabla */}
                {loading ? (
                    <div className="p-12 text-center opacity-60">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                    </div>
                ) : (
                    <div className="border overflow-x-auto" style={{ borderColor: 'var(--border)' }} data-testid="pricing-matrix-table">
                        <table className="w-full text-sm min-w-[900px]">
                            <thead>
                                <tr style={{ background: '#111', color: '#fff' }}>
                                    <th className="text-left p-3 text-[10px] uppercase tracking-widest font-bold">Rubro</th>
                                    <th className="text-left p-3 text-[10px] uppercase tracking-widest font-bold">Riesgo</th>
                                    <th className="text-right p-3 text-[10px] uppercase tracking-widest font-bold">Base MXN</th>
                                    {data.packages.map((p) => (
                                        <th key={p.code} className="text-right p-3 text-[10px] uppercase tracking-widest font-bold"
                                            style={{ borderLeft: `3px solid ${p.color}` }}>
                                            {p.code}
                                        </th>
                                    ))}
                                    <th className="text-center p-3 text-[10px] uppercase tracking-widest font-bold">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r) => {
                                    const risk = RISK_META[r.risk_level] || RISK_META.medium;
                                    const RIcon = risk.icon;
                                    return (
                                        <tr key={r.rubro_id} className="border-t hover:bg-black/[0.02]"
                                            style={{ borderColor: 'var(--border)' }}
                                            data-testid={`pricing-row-${r.rubro_id}`}>
                                            <td className="p-3">
                                                <div className="font-semibold">{r.rubro_name}</div>
                                                <div className="text-[10px] opacity-60">{r.category}</div>
                                            </td>
                                            <td className="p-3">
                                                <div className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1"
                                                    style={{ background: `${risk.color}15`, color: risk.color }}>
                                                    <RIcon className="w-3.5 h-3.5" />
                                                    {risk.label}
                                                </div>
                                            </td>
                                            <td className="p-3 text-right font-mono font-bold">{money(r.base_price_mxn)}</td>
                                            {data.packages.map((p) => (
                                                <td key={p.code} className="p-3 text-right"
                                                    style={{ borderLeft: `2px solid ${p.color}30` }}>
                                                    <div className="font-bold text-sm" style={{ color: p.color }}>
                                                        {money(r.prices[p.code]?.per_audit_mxn)}
                                                    </div>
                                                    <div className="text-[10px] opacity-60">
                                                        ×8 = {money(r.prices[p.code]?.cycle_8_monthly_mxn)}
                                                    </div>
                                                </td>
                                            ))}
                                            <td className="p-3 text-center">
                                                <button onClick={() => setEditRow(r)}
                                                    data-testid={`pricing-edit-${r.rubro_id}`}
                                                    className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 border-2 hover:bg-black hover:text-white"
                                                    style={{ borderColor: 'var(--border)' }}>
                                                    <Edit3 className="w-3 h-3" /> Editar
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={4 + data.packages.length} className="p-8 text-center opacity-60">
                                            Sin resultados.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {editRow && (
                <EditPricingModal
                    row={editRow}
                    onClose={() => setEditRow(null)}
                    onSaved={() => { setEditRow(null); load(); }}
                />
            )}
        </AdminLayout>
    );
}

function KpiBox({ label, value, color }) {
    return (
        <div className="p-3 border" style={{ borderColor: 'var(--border)' }}>
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">{label}</div>
            <div className="text-2xl font-display font-black" style={{ color: color || 'inherit' }}>{value}</div>
        </div>
    );
}

function EditPricingModal({ row, onClose, onSaved }) {
    const [risk, setRisk] = useState(row.risk_level || 'medium');
    const [price, setPrice] = useState(row.base_price_mxn || 0);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/rubros/${row.rubro_id}/pricing`, {
                risk_level: risk,
                base_price_mxn: Number(price),
            });
            toast.success('Precio actualizado');
            onSaved();
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al guardar'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.55)' }} onClick={onClose}>
            <div className="max-w-md w-full border max-h-[calc(100vh-2rem)] overflow-y-auto" style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}
                onClick={(e) => e.stopPropagation()}
                data-testid="edit-pricing-modal">
                <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Editar precio</div>
                        <div className="font-display font-black text-xl">{row.rubro_name}</div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-black/5"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <div>
                        <label className="text-[10px] uppercase tracking-widest font-bold opacity-60 mb-2 block">
                            Nivel de riesgo
                        </label>
                        <div className="flex gap-2">
                            {Object.entries(RISK_META).map(([v, meta]) => {
                                const Icn = meta.icon;
                                const active = risk === v;
                                return (
                                    <button key={v} onClick={() => setRisk(v)}
                                        data-testid={`risk-btn-${v}`}
                                        className="flex-1 p-3 border-2 flex flex-col items-center gap-1"
                                        style={{
                                            borderColor: active ? meta.color : 'var(--border)',
                                            background: active ? `${meta.color}15` : 'transparent',
                                            color: active ? meta.color : 'inherit',
                                        }}>
                                        <Icn className="w-4 h-4" />
                                        <span className="text-xs font-bold">{meta.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest font-bold opacity-60 mb-2 block">
                            Precio base por auditoría (MXN)
                        </label>
                        <input type="number" min="0" step="100"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            data-testid="base-price-input"
                            className="w-full border-2 p-3 text-lg font-bold outline-none focus:border-black"
                            style={{ borderColor: 'var(--border)', background: 'transparent' }} />
                        <div className="text-[10px] opacity-60 mt-2 space-y-0.5">
                            <div>ESSENTIAL (×1.0): <b>{money(price)}</b> · ciclo 8 = <b>{money(price * 8)}</b></div>
                            <div>PRO (×1.4): <b>{money(price * 1.4)}</b> · ciclo 8 = <b>{money(price * 1.4 * 8)}</b></div>
                            <div>ELITE (×2.0): <b>{money(price * 2)}</b> · ciclo 8 = <b>{money(price * 2 * 8)}</b></div>
                        </div>
                    </div>
                </div>
                <div className="p-5 border-t flex justify-end gap-2" style={{ borderColor: 'var(--border)' }}>
                    <button onClick={onClose}
                        className="px-4 py-2 border-2 text-sm font-bold"
                        style={{ borderColor: 'var(--border)' }}>Cancelar</button>
                    <button onClick={save} disabled={saving}
                        data-testid="save-pricing-btn"
                        className="px-4 py-2 text-white text-sm font-bold inline-flex items-center gap-1 disabled:opacity-50"
                        style={{ background: '#7C6BEF' }}>
                        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        Guardar
                    </button>
                </div>
            </div>
        </div>
    );
}
